import React, { useState } from 'react';
import { Grid, Typography, Box } from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';
import { useApi } from '@backstage/core-plugin-api';
import { Progress, Table } from '@backstage/core-components';
import Alert from '@material-ui/lab/Alert';
import { Octokit } from '@octokit/rest';
import { useAsync } from 'react-use';
import { DateTime } from 'luxon';
import { useProjectName } from '../useProjectName.esm.js';
import { useProjectEntity } from '../useProjectEntity.esm.js';
import { UpdateSeverityStatusModal } from '../UpdateSeverityStatusModal/UpdateSeverityStatusModal.esm.js';
import { StateFilterComponent } from './components/StateFilterComponent/StateFilterComponent.esm.js';
import { useUrl } from '../useUrl.esm.js';
import { getSeverityBadge } from '../utils.esm.js';
import { useEntity } from '@backstage/plugin-catalog-react';
import { scmAuthApiRef } from '@backstage/integration-react';

const getElapsedTime = (start) => {
  return DateTime.fromISO(start).toRelative();
};
const SecurityInsightsTable = () => {
  const [insightsStatusFilter, setInsightsStatusFilter] = useState("open");
  const [filteredTableData, setFilteredTableData] = useState(
    []
  );
  const [tableData, setTableData] = useState([]);
  const { entity } = useEntity();
  const { owner, repo } = useProjectEntity(entity);
  const projectName = useProjectName(entity);
  const scmAuth = useApi(scmAuthApiRef);
  const { baseUrl, hostname } = useUrl(entity);
  const { value, loading, error } = useAsync(async () => {
    const credentials = await scmAuth.getCredentials({
      additionalScope: {
        customScopes: { github: ["repo"] }
      },
      url: `https://${hostname}`,
      optional: true
    });
    const octokit = new Octokit({ auth: credentials.token });
    const response = await octokit.request(
      "GET /repos/{owner}/{repo}/code-scanning/alerts",
      {
        baseUrl,
        owner,
        repo,
        per_page: 100
      }
    );
    const data = response.data;
    setTableData(data);
    if (insightsStatusFilter) {
      setFilteredTableData(
        data.filter((entry) => entry.state === insightsStatusFilter)
      );
    }
    return data;
  }, []);
  const generatedColumns = [
    {
      title: "ID",
      field: "number",
      width: "50px",
      highlight: true,
      render: (row) => /* @__PURE__ */ React.createElement(Box, { fontWeight: "fontWeightBold" }, /* @__PURE__ */ React.createElement("a", { target: "_blank", rel: "noopener noreferrer", href: row.html_url }, "#", row.number))
    },
    {
      title: "Description",
      field: "rule.description",
      highlight: true,
      render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, row?.rule?.description)
    },
    {
      title: "Severity",
      field: "rule.severity",
      highlight: true,
      render: (row) => {
        const severityLevel = row?.rule?.severity;
        return /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center", fontWeight: "fontWeightLight" }, severityLevel && getSeverityBadge(severityLevel));
      }
    },
    {
      title: "State",
      field: "state",
      highlight: true,
      render: (row) => row.state && row.number && /* @__PURE__ */ React.createElement(
        UpdateSeverityStatusModal,
        {
          owner,
          repo,
          severityData: row.state,
          id: row.number,
          tableData,
          setTableData,
          entity
        }
      )
    },
    {
      title: "Tool",
      field: "tool.name",
      highlight: true,
      render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, row?.tool?.name)
    },
    {
      title: "Detected",
      field: "created_at",
      highlight: true,
      render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, getElapsedTime(row.created_at))
    }
  ];
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, /* @__PURE__ */ React.createElement(Grid, { container: true, direction: "row", spacing: 3 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, null, "Failed to retrieve security insights information from GitHub. Security Insights plugin may require administrator access to display data correctly")), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, null, "Error message: ", error.message))));
  }
  return value ? /* @__PURE__ */ React.createElement(
    Table,
    {
      isLoading: loading,
      options: {
        paging: true,
        padding: "dense"
      },
      data: insightsStatusFilter !== null && filteredTableData || tableData,
      title: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center" }, /* @__PURE__ */ React.createElement(GitHubIcon, null), /* @__PURE__ */ React.createElement(Box, { mr: 1 }), /* @__PURE__ */ React.createElement(Typography, { variant: "h6" }, projectName)), /* @__PURE__ */ React.createElement(
        StateFilterComponent,
        {
          insightsStatusFilter,
          value,
          setInsightsStatusFilter,
          setFilteredTableData
        }
      )),
      columns: generatedColumns
    }
  ) : null;
};

export { SecurityInsightsTable };
//# sourceMappingURL=SecurityInsightsTable.esm.js.map
